/* eslint-disable consistent-return */
import * as React from 'react';
import { Editor, Node, Range, Transforms } from 'slate';
import { ReactEditor, RenderElementProps } from 'slate-react';

import { SlatePlugin } from '../../../../slatePlugins/slatePlugins.types';
import { isInsideOfElement } from '../../../DolEditor/helpers/selection';
import { QuestionStyleElement } from './Element/QuestionStyleElement';
import { ELEMENT_QUESTION_STYLE, QuestionStyleOptions } from './QuestionStyle.types';

function removeCarriageReturn(text: string) {
  return text.replace(/(\r\n|\n|\r)/gm, '');
}

const deserializeQuestionStyle = (el) => {
  if (el && el?.classList?.contains('question-style-element')) {
    return { type: ELEMENT_QUESTION_STYLE };
  }
  return undefined;
};

export const QuestionStylePlugin = (options: QuestionStyleOptions = {}): SlatePlugin => {
  return {
    renderElement: (renderProps: RenderElementProps) => {
      const { element } = renderProps;
      if (element.type === ELEMENT_QUESTION_STYLE) {
        return <QuestionStyleElement {...renderProps} {...options} />;
      }
    },
    inlineTypes: [ELEMENT_QUESTION_STYLE],
    deserialize: {
      element: [
        {
          type: 'question-style',
          deserialize: deserializeQuestionStyle,
        },
      ],
    },
  };
};

export const withQuestionStyleEditorChain =
  () =>
  <T extends ReactEditor>(editor: T) => {
    const { deleteBackward, deleteForward, insertBreak, normalizeNode, apply, insertData } = editor;

    editor.normalizeNode = (entry) => {
      const [node, path] = entry as [any, any[]];
      if (node?.type === ELEMENT_QUESTION_STYLE) {
        const text = Node.string(node);
        if (text[text.length - 1] !== ' ') {
          Transforms.insertText(editor, `${text} `, { at: path });
          return;
        }
      }
      normalizeNode(entry);
    };

    editor.deleteBackward = (unit) => {
      if (editor.selection) {
        const isCollapse = Range.isCollapsed(editor.selection);
        if (isCollapse) {
          const beforePath = Editor.before(editor, editor.selection, { unit });
          const [node] = Editor.nodes(editor, {
            at: editor.selection,
            match: (n: any) => n?.type === ELEMENT_QUESTION_STYLE,
          });
          const [beforeNode] = Editor.nodes(editor, {
            at: beforePath,
            match: (n: any) => n?.type === ELEMENT_QUESTION_STYLE,
          });
          // delete at end of node
          if (!node && beforeNode) {
            const [, questionTypeNodePath] = beforeNode;
            Transforms.removeNodes(editor, { at: questionTypeNodePath });
            return;
          }
          // delete at start of node
          if (node && !beforeNode) {
            const [, questionTypeNodePath] = node;
            Transforms.removeNodes(editor, { at: questionTypeNodePath });
          }
        }
      }
      deleteBackward(unit);
    };

    editor.deleteForward = (unit) => {
      deleteForward(unit);
    };

    editor.insertData = (data) => {
      if (isInsideOfElement(editor, ELEMENT_QUESTION_STYLE)) {
        const text = data.getData('text/plain');
        Transforms.insertText(editor, removeCarriageReturn(text));
      } else {
        insertData(data);
      }
    };

    editor.insertBreak = () => {
      if (isInsideOfElement(editor, ELEMENT_QUESTION_STYLE)) {
        return;
      }
      insertBreak();
    };

    editor.apply = (operation) => {
      if (
        editor.selection &&
        operation.type === 'set_selection' &&
        Range.isCollapsed(editor.selection) &&
        operation?.newProperties?.anchor?.path
      ) {
        try {
          const { anchor } = operation.newProperties;
          const inlineNode = Editor.above(editor, {
            at: anchor,
            match: (n) => Editor.isInline(editor, n as Element),
            mode: 'highest',
          }) as any;

          const endInline = Editor.end(editor, anchor.path);
          if (inlineNode && inlineNode[0]?.type === ELEMENT_QUESTION_STYLE) {
            const [node] = inlineNode;
            const text = Node.string(node);

            if (endInline.offset === anchor.offset && text.trim().length > 0) {
              Transforms.move(editor, { unit: 'offset', distance: 2 });
              return;
            }
          }
          // eslint-disable-next-line no-empty
        } catch (ex) {}
      }
      apply(operation);
    };

    return editor;
  };
